import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CreditCardIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { billingAPI } from '../../services/api';

const billingIntervals = [
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'yearly', label: 'Yearly' },
];

const statusColors = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  trialing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  past_due: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  canceled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  unpaid: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export default function BillingPage() {
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState('monthly');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchData();

    // Check for success/cancel from Stripe redirect
    if (searchParams.get('success') === 'true') {
      setMessage({ type: 'success', text: 'Payment successful! Your subscription is now active.' });
    } else if (searchParams.get('canceled') === 'true') {
      setMessage({ type: 'error', text: 'Payment was canceled. Please try again.' });
    }
  }, [searchParams]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subRes, plansRes, paymentsRes, invoicesRes] = await Promise.all([
        billingAPI.getSubscription().catch(() => ({ data: {} })),
        billingAPI.getPlans(),
        billingAPI.getPayments({ limit: 5 }).catch(() => ({ data: {} })),
        billingAPI.getInvoices().catch(() => ({ data: {} })),
      ]);

      // Handle nested response structure from API client
      setSubscription(subRes.data?.data?.subscription || subRes.data?.subscription || null);
      const fetchedPlans = plansRes.data?.data?.plans || plansRes.data?.plans || [];
      setPlans(fetchedPlans);
      setPayments(paymentsRes.data?.data?.payments || paymentsRes.data?.payments || []);
      setInvoices(invoicesRes.data?.data?.invoices || invoicesRes.data?.invoices || []);

      // If coming from pricing page with plan param, show upgrade modal
      const planParam = searchParams.get('plan');
      const intervalParam = searchParams.get('interval');
      if (planParam && fetchedPlans.length > 0) {
        // Find plan by slug or id
        const targetPlan = fetchedPlans.find(p =>
          p.slug === planParam || p.id?.toString() === planParam
        );
        if (targetPlan) {
          setSelectedPlan(targetPlan);
          setShowUpgradeModal(true);
          if (intervalParam) {
            setSelectedInterval(intervalParam);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      setMessage({ type: 'error', text: 'Failed to load billing information' });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (planId, interval) => {
    setActionLoading(true);
    try {
      const response = await billingAPI.createCheckout({
        planId,
        billingInterval: interval,
      });

      // Handle nested response structure from API client
      const url = response.data?.data?.url || response.data?.url;
      if (!url) {
        throw new Error('No checkout URL returned');
      }
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Checkout error:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to start checkout' });
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setActionLoading(true);
    try {
      const response = await billingAPI.createPortalSession();
      const url = response.data?.data?.url || response.data?.url;
      if (!url) {
        throw new Error('No portal URL returned');
      }
      window.location.href = url;
    } catch (error) {
      console.error('Portal error:', error);
      setMessage({ type: 'error', text: 'Failed to open billing portal' });
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access at the end of your billing period.')) {
      return;
    }

    setActionLoading(true);
    try {
      await billingAPI.cancelSubscription({ immediately: false });
      setMessage({ type: 'success', text: 'Subscription will be canceled at the end of your billing period.' });
      fetchData();
    } catch (error) {
      console.error('Cancel error:', error);
      setMessage({ type: 'error', text: 'Failed to cancel subscription' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await billingAPI.reactivateSubscription();
      setMessage({ type: 'success', text: 'Subscription reactivated!' });
      fetchData();
    } catch (error) {
      console.error('Reactivate error:', error);
      setMessage({ type: 'error', text: 'Failed to reactivate subscription' });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading billing..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Billing & Subscription</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          Manage your subscription, payment methods, and billing history.
        </p>
      </div>

      {/* Message Alert */}
      {message.text && (
        <div
          className={`rounded-md p-4 ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/50 text-green-800 dark:text-green-200'
              : 'bg-red-50 dark:bg-red-900/50 text-red-800 dark:text-red-200'
          }`}
        >
          <div className="flex">
            {message.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationCircleIcon className="h-5 w-5 mr-2" />
            )}
            <p>{message.text}</p>
            <button
              onClick={() => setMessage({ type: '', text: '' })}
              className="ml-auto"
            >
              <XCircleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Current Subscription */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Current Subscription
          </h2>

          {subscription ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-bold text-slate-900 dark:text-white">
                      {subscription.plan?.name} Plan
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        statusColors[subscription.status] || statusColors.active
                      }`}
                    >
                      {subscription.status}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    {subscription.billingInterval} billing
                  </p>
                </div>
                <div className="text-right">
                  {subscription.plan?.priceMonthly && (
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {formatCurrency(subscription.plan.priceMonthly)}
                      <span className="text-sm font-normal text-slate-500">/month</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Billing Period */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Current Period</span>
                    <p className="text-slate-900 dark:text-white">
                      {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                    </p>
                  </div>
                  {subscription.trialEnd && new Date(subscription.trialEnd) > new Date() && (
                    <div>
                      <span className="text-slate-500 dark:text-slate-400">Trial Ends</span>
                      <p className="text-slate-900 dark:text-white">{formatDate(subscription.trialEnd)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Method */}
              {subscription.paymentMethod && (
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                  <div className="flex items-center gap-3">
                    <CreditCardIcon className="h-8 w-8 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {subscription.paymentMethod.brand?.toUpperCase()} •••• {subscription.paymentMethod.last4}
                      </p>
                      <p className="text-xs text-slate-500">
                        Expires {subscription.paymentMethod.expMonth}/{subscription.paymentMethod.expYear}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cancellation Notice */}
              {subscription.cancelAtPeriodEnd && (
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                  <div className="bg-yellow-50 dark:bg-yellow-900/50 rounded-md p-3">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      Your subscription will be canceled on {formatDate(subscription.currentPeriodEnd)}.
                      <button
                        onClick={handleReactivate}
                        className="ml-2 underline font-medium"
                        disabled={actionLoading}
                      >
                        Reactivate
                      </button>
                    </p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 flex gap-3">
                <Button onClick={handleManageBilling} disabled={actionLoading}>
                  <CreditCardIcon className="h-4 w-4 mr-2" />
                  Manage Payment Method
                </Button>
                <Button variant="outline" onClick={() => setShowUpgradeModal(true)}>
                  Change Plan
                </Button>
                {!subscription.cancelAtPeriodEnd && subscription.status === 'active' && (
                  <Button
                    variant="outline"
                    onClick={handleCancelSubscription}
                    disabled={actionLoading}
                    className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                  >
                    Cancel Subscription
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCardIcon className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No Active Subscription
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Choose a plan to get started with CyberActions.
              </p>
              <Button onClick={() => setShowUpgradeModal(true)}>
                Choose a Plan
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Available Plans (Upgrade Modal) */}
      {showUpgradeModal && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {subscription ? 'Change Plan' : 'Choose a Plan'}
              </h2>
              <button onClick={() => setShowUpgradeModal(false)} className="text-slate-400 hover:text-slate-600">
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Billing Interval Selector */}
            <div className="mb-6">
              <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
                {billingIntervals.map((interval) => (
                  <button
                    key={interval.id}
                    onClick={() => setSelectedInterval(interval.id)}
                    className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                      selectedInterval === interval.id
                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    {interval.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.filter(p => p.slug !== 'enterprise').map((plan) => {
                const price = selectedInterval === 'yearly'
                  ? plan.priceYearly
                  : selectedInterval === 'quarterly'
                  ? plan.priceQuarterly
                  : plan.priceMonthly;

                const isCurrentPlan = subscription?.plan?.id === plan.id;

                return (
                  <div
                    key={plan.slug}
                    className={`border rounded-lg p-4 ${
                      plan.isPopular
                        ? 'border-primary-500 ring-1 ring-primary-500'
                        : 'border-slate-200 dark:border-slate-700'
                    } ${isCurrentPlan ? 'bg-slate-50 dark:bg-slate-800' : ''}`}
                  >
                    {plan.isPopular && (
                      <span className="inline-block bg-primary-600 text-white text-xs font-medium px-2 py-1 rounded mb-2">
                        Recommended
                      </span>
                    )}
                    <h3 className="font-semibold text-slate-900 dark:text-white">{plan.name}</h3>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white mt-2">
                      {formatCurrency(price)}
                      <span className="text-sm font-normal text-slate-500">
                        /{selectedInterval === 'yearly' ? 'year' : selectedInterval === 'quarterly' ? 'quarter' : 'month'}
                      </span>
                    </p>
                    <p className="text-sm text-slate-500 mt-1">{plan.description}</p>
                    <Button
                      variant={plan.isPopular ? 'primary' : 'outline'}
                      className="w-full mt-4"
                      disabled={isCurrentPlan || actionLoading}
                      onClick={() => handleCheckout(plan.id, selectedInterval)}
                    >
                      {isCurrentPlan ? 'Current Plan' : 'Select Plan'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* Payment History */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Payment History
          </h2>

          {payments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Receipt
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">
                        {formatDate(payment.paidAt || payment.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                        {payment.description || 'Subscription payment'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">
                        {formatCurrency(payment.amount, payment.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            payment.status === 'succeeded'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : payment.status === 'failed'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          }`}
                        >
                          {payment.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {payment.receiptUrl && (
                          <a
                            href={payment.receiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-700 text-sm"
                          >
                            View
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-slate-500 dark:text-slate-400 py-4">
              No payment history yet.
            </p>
          )}
        </div>
      </Card>

      {/* Invoices */}
      {invoices.length > 0 && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Invoices
            </h2>

            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <DocumentTextIcon className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        Invoice #{invoice.number}
                      </p>
                      <p className="text-xs text-slate-500">{formatDate(invoice.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                      {formatCurrency(invoice.amount, invoice.currency)}
                    </span>
                    {invoice.pdfUrl && (
                      <a
                        href={invoice.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 text-sm"
                      >
                        Download PDF
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
